<template>
  <dialog-modal :show="isShow">
    <template #title>
      <div class="flex font-bold justify-center">
        無料会員登録
      </div>
    </template>
    <template #content>
      <div
        v-if="!isRegisterEnd"
        class="mx-auto flex justify-center">
        <div class="mx-auto">
          <form @submit.prevent="signUp">
            <label for="email">
              メールアドレス
            </label>
            <input
              v-model="email"
              class="block mt-2 mb-2 w-80 border-1 border-gray-500 rounded"
              type="email"
              required
              placeholder="メールアドレス"
              autocomplete="off" />
            <div
              v-if="emailFormErrorText"
              class="text-red-500 mb-5">
              ※{{ emailFormErrorText }}
            </div>

            <label for="password">
              パスワード
            </label>
            <input
              v-model="password"
              class="block mt-2 mb-2 w-80 border-1 border-gray-500 rounded"
              type="password"
              required
              placeholder="パスワード(6文字以上)"
              autocomplete="off" />
            <div
              v-if="passwordFormErrorText"
              class="text-red-500 mb-5">
              ※{{ passwordFormErrorText }}
            </div>

            <label for="confirmationPassword">
              確認用パスワード
            </label>
            <input
              v-model="confirmationPassword"
              class="block mt-2 mb-2 w-80 border-1 border-gray-500 rounded"
              type="password"
              required
              placeholder="確認用パスワード"
              autocomplete="off" />
            <div
              v-if="confirmationPasswordFormErrorText"
              class="whitespace-pre-line text-red-500 mb-5">
              ※{{ confirmationPasswordFormErrorText }}
            </div>

            <div
              v-if="signUpErrorText"
              class="whitespace-pre-line text-red-500 my-5">
              {{ signUpErrorText }}
            </div>

            <button
              :disabled="isProcessing"
              class="flex justify-self-end text-white text-center text-base bg-CLIENT_PRIMARY_PINK rounded-xl mx-auto mt-2 px-10 py-2 hover:bg-CLIENT_PRIMARY_PINK_HOVER duration-500 disabled:bg-gray-300 disabled:cursor-not-allowed">
              登録
            </button>
          </form>
        </div>
      </div>
      <div
        v-else
        class="text-center">
        <p>確認用メールを送信しました。</p>
        <p>メール内のリンクにアクセスして、確認をしてください。</p>
        <p>5秒後に自動的にログインします。お待ちください。</p>
      </div>
    </template>

    <template #footer>
      <button
        class="text-CLIENT_PRIMARY_PINK text-center border-2 border-CLIENT_PRIMARY_PINK text-base rounded-xl px-2 py-2 ml-4 bg-white hover:bg-gray-100 duration-500"
        @click.prevent="$emit('close')">
        閉じる
      </button>
    </template>
  </dialog-modal>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import firebase from 'firebase/compat/app'
import DialogModal from '@/Common/DialogModal'
import firebaseApp from '@/Constants/configFirebase'
import 'firebase/compat/auth'
import REGISTER_ERROR_MESSAGES from '@/Constants/registerErrors'
import { EmailRegex } from '@/Constants/regularExpression'

import {
  getAuth,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
} from 'firebase/auth'

const props = defineProps({
  isShow: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])

const auth = getAuth(firebaseApp)

const isOpen = ref(false)

const email = ref('')
const password = ref('')
const confirmationPassword = ref('')

const emailFormErrorText = ref('')
const passwordFormErrorText = ref('')
const confirmationPasswordFormErrorText = ref('')
const signUpErrorText = ref('')

const isRegisterEnd = ref(false)

const isProcessing = ref(false)

const actionCodeSettings = {
  // 環境変数の値を読み込む
  url: route('portal.index'),
}

const signUp = () => {
  // 未入力＆フォーマットチェック
  if (!email.value.length) {
    emailFormErrorText.value = REGISTER_ERROR_MESSAGES.emptyEmail
  } else if (!email.value.match(EmailRegex)) {
    emailFormErrorText.value = REGISTER_ERROR_MESSAGES.invalidEmail
  } else {
    emailFormErrorText.value = ''
  }
  if (password.value.length < 6) {
    passwordFormErrorText.value = REGISTER_ERROR_MESSAGES.password
  } else {
    passwordFormErrorText.value = ''
  }
  if (password.value !== confirmationPassword.value) {
    confirmationPasswordFormErrorText.value = REGISTER_ERROR_MESSAGES.confirmationPassword
  } else {
    confirmationPasswordFormErrorText.value = ''
  }

  // ERRORがあれば、処理を中断する
  if (emailFormErrorText.value || passwordFormErrorText.value || confirmationPasswordFormErrorText.value) {
    return false
  }

  // 多重ポストを避ける
  if (isProcessing.value) return false
  isProcessing.value = true

  createUserWithEmailAndPassword(auth, email.value, password.value)
    .then(async () => {
      // 確認用メール送信
      await sendEmailVerification(auth.currentUser, actionCodeSettings)
        .then(async () => {
          await signInWithEmailAndPassword(auth, email.value, password.value)
          const userToken = await auth.currentUser.getIdToken(true)
          const userEmail = auth.currentUser.email
          isRegisterEnd.value = true
          isProcessing.value = false

          Inertia.visit('/login', {
            method: 'post',
            data: {
              email: userEmail,
              password: password.value,
              idToken: userToken,
            },
          })
        })
        .catch((e) => {
          console.log(e)
          isProcessing.value = false
          signUpErrorText.value = e.code
        })
    }).catch(error => {
      console.log(error)
      isProcessing.value = false
      if (error.code === 'auth/email-already-in-use') {
        signUpErrorText.value = '既に登録済みのメールアドレスです。'
      } else {
        signUpErrorText.value = error.code
      }
    })
}

</script>
